import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import {
  ThemeEvents,
  CartUpdateEvent,
  CustomizerCartPendingStartEvent,
  CustomizerCartPendingEndEvent,
} from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number | null} */
  #storedItemCount = null;

  /** @type {((event: Event) => void) | null} */
  #cartTriggerClickHandler = null;

  get #isDotOnly() {
    return this.dataset.dotOnly === 'true';
  }

  /** @type {number} */
  get currentCartCount() {
    if (this.#isDotOnly && this.#storedItemCount != null) {
      return this.#storedItemCount;
    }

    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    if (this.#isDotOnly) {
      this.#storedItemCount = value;
      this.refs.cartBubbleCount.textContent = '';
      return;
    }

    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#isDotOnly) {
      const initial = parseInt(this.dataset.initialCount ?? '0', 10);
      this.#storedItemCount = Number.isFinite(initial) ? initial : 0;
      this.refs.cartBubbleCount.textContent = '';
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener(CustomizerCartPendingStartEvent.eventName, this.#onPendingStart);
    document.addEventListener(CustomizerCartPendingEndEvent.eventName, this.#onPendingEnd);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener(CustomizerCartPendingStartEvent.eventName, this.#onPendingStart);
    document.removeEventListener(CustomizerCartPendingEndEvent.eventName, this.#onPendingEnd);
    window.removeEventListener('pageshow', this.onPageShow);
    this.#setPending(false);
  }

  /**
   * @returns {HTMLAnchorElement | HTMLButtonElement | null}
   */
  #getCartTrigger() {
    const trigger = this.closest('a, button[data-testid="cart-drawer-trigger"]');
    return trigger instanceof HTMLAnchorElement || trigger instanceof HTMLButtonElement ? trigger : null;
  }

  #onPendingStart = () => {
    this.#setPending(true);
  };

  #onPendingEnd = () => {
    if (document.documentElement.dataset.customizerCartRedirecting === 'true') {
      return;
    }

    this.#setPending(false);
  };

  /**
   * @param {boolean} active
   */
  #setPending(active) {
    const trigger = this.#getCartTrigger();
    if (!trigger) return;

    if (active) {
      trigger.setAttribute('aria-disabled', 'true');
      trigger.classList.add('header-actions__cart-trigger--pending');
      document.documentElement.dataset.customizerCartPending = 'true';

      this.#cartTriggerClickHandler = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      trigger.addEventListener('click', this.#cartTriggerClickHandler, true);
      return;
    }

    trigger.removeAttribute('aria-disabled');
    trigger.classList.remove('header-actions__cart-trigger--pending');
    delete document.documentElement.dataset.customizerCartPending;

    if (this.#cartTriggerClickHandler) {
      trigger.removeEventListener('click', this.#cartTriggerClickHandler, true);
      this.#cartTriggerClickHandler = null;
    }
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const resourceCount = event.detail.resource?.item_count;
    const dataCount = event.detail.data?.itemCount;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    if (typeof resourceCount === 'number' && Number.isFinite(resourceCount)) {
      this.renderCartBubble(resourceCount, false);
      return;
    }

    if (typeof dataCount === 'number' && Number.isFinite(dataCount) && !comingFromProductForm) {
      this.renderCartBubble(dataCount, false);
      return;
    }

    this.renderCartBubble(dataCount ?? 0, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const resolvedCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    const hasItems = resolvedCount > 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', !hasItems);
    this.refs.cartBubble.classList.toggle('visually-hidden', !hasItems);

    this.currentCartCount = resolvedCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', hasItems);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(resolvedCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || !hasItems) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.#isDotOnly ? String(this.#storedItemCount ?? 0) : this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
